import { FC, useEffect, useMemo, useState } from 'react'
import { sortedObjects } from './utils'
import { DragLineItemProps } from './types'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import LineItem from './LineItem'

interface DragLineListProps {
  items: DragLineItemProps[]
  listOrder: (number | string)[]
  updateListOrdering: (newListOrder: (string | number)[]) => void
}

const DragLineList: FC<DragLineListProps> = ({
  items,
  listOrder,
  updateListOrdering,
}) => {
  const [activeId, setActiveId] = useState<number | string | null>(null)
  const defaultListOrder = useMemo(() => items.map((val) => val.id), [items])

  useEffect(() => {
    if (
      Array.isArray(listOrder) &&
      listOrder.every(
        (item) => typeof item === 'number' || typeof item === 'string',
      )
    ) {
      const uniqueElements = defaultListOrder.filter(
        (item) => !listOrder.includes(item),
      )
      if (uniqueElements.length === 0) return

      const newOrdering = listOrder.concat(uniqueElements)
      updateListOrdering(newOrdering)

      updateListOrdering(newOrdering)
      console.log('Initial saving of the sorting')
    } else {
      updateListOrdering(defaultListOrder)
      updateListOrdering(defaultListOrder)
      console.log('Initial saving of the sorting')
    }
  }, [items])

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  )

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null)
    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    // Get indexes of active and over columns (lines)
    const oldIndex = listOrder.indexOf(Number(active.id))
    const newIndex = listOrder.indexOf(Number(over.id))

    const newSortOrder = arrayMove(listOrder, oldIndex, newIndex)
    updateListOrdering(newSortOrder)
  }

  const sortedItems = sortedObjects(items, listOrder)
  const activeItem = sortedItems.find((val) => val.id === activeId)

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={listOrder} strategy={verticalListSortingStrategy}>
        {sortedItems.map((props) => (
          <LineItem key={props.id} {...props} />
        ))}
        <DragOverlay>
          {activeItem ? <LineItem {...activeItem} overlayItem={true} /> : null}
        </DragOverlay>
      </SortableContext>
    </DndContext>
  )
}

export default DragLineList
